// NOTICE: KAPTION-INTEGRATION
// Durable WhatsApp outbound message batches — the open-batch read for the
// currently selected conversation, plus the flush/discard/update mutations.
//
// Extracted out of the upstream MessageList.vue so the Kaption footprint in that
// file is one import and one call.
//
// Both availability gates below mirror
// Api::V1::Accounts::Conversations::KaptionMessageBatchesController exactly:
//
//   before_action :ensure_batching_enabled       -> 403 message_batching_disabled
//       KaptionFeatureFlagsService.enabled?('KAPTION_MESSAGE_BATCHING')
//   before_action :ensure_whatsapp_conversation  -> 422 message_batching_requires_whatsapp
//       conversation.inbox.channel_type == 'Channel::Whatsapp'
//
// Without them the dashboard asked for a batch on every conversation open, on
// every channel, and banked a refusal every single time — the flag is
// experimental and defaults to false, so the read could not succeed anywhere.
// ReplyBox.vue already gates the write side (addMessageBatchFragment) on the
// same flag plus a WhatsApp check; this is the read side of that gate.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useInbox } from 'dashboard/composables/useInbox';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import * as KaptionMessageBatchesAPI from 'dashboard/api/kaptionMessageBatches';
import { emitter } from 'shared/helpers/mitt';

export const MESSAGE_BATCH_UPDATED_EVENT = 'kaption-message-batch-updated';

/**
 * Tracks the open outbound batch for the selected conversation.
 *
 * @returns {Object} batch state and the mutations the timeline card needs
 */
export function useKaptionMessageBatch() {
  const currentChat = useMapGetter('getSelectedChat');
  const currentAccountId = useMapGetter('getCurrentAccountId');
  const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
  const { inbox } = useInbox();

  const messageBatch = ref(null);
  const messageBatchBusy = ref(false);

  // NOTICE: KAPTION-INTEGRATION - Strictly Channel::Whatsapp, matching the
  // controller. Deliberately NOT useInbox().isAWhatsAppChannel, which also
  // matches Twilio WhatsApp — the server refuses that with a 422.
  const isBatchingAvailable = computed(
    () =>
      isKaptionFlagEnabled('KAPTION_MESSAGE_BATCHING') &&
      // whatsapp-message-merging replaces this; never both at once.
      !isKaptionFlagEnabled('KAPTION_MESSAGE_MERGING') &&
      inbox.value?.channelType === INBOX_TYPES.WHATSAPP
  );

  const applyBatch = response => {
    const batch = response?.data?.batch;
    messageBatch.value = batch?.status === 'open' ? batch : null;
  };

  const loadMessageBatch = async () => {
    // NOTICE: KAPTION-INTEGRATION - never ask for a batch the server will refuse.
    if (!isBatchingAvailable.value) {
      messageBatch.value = null;
      return;
    }
    if (!currentChat.value?.id || !currentAccountId.value) return;
    try {
      applyBatch(
        await KaptionMessageBatchesAPI.getMessageBatch(
          currentAccountId.value,
          currentChat.value.id
        )
      );
    } catch {
      messageBatch.value = null;
    }
  };

  const mutateBatch = async (method, payload) => {
    if (!messageBatch.value) return;
    messageBatchBusy.value = true;
    try {
      applyBatch(
        await method(
          currentAccountId.value,
          currentChat.value.id,
          messageBatch.value.id,
          payload
        )
      );
      emitter.emit(MESSAGE_BATCH_UPDATED_EVENT);
    } finally {
      messageBatchBusy.value = false;
    }
  };

  const flushBatch = () =>
    mutateBatch(KaptionMessageBatchesAPI.flushMessageBatch);
  const discardBatch = () =>
    mutateBatch(KaptionMessageBatchesAPI.discardMessageBatch);
  const updateBatch = content =>
    mutateBatch(KaptionMessageBatchesAPI.updateMessageBatch, { content });

  // NOTICE: KAPTION-INTEGRATION - isBatchingAvailable is watched alongside the
  // conversation id because the inbox record (and the flag cache) can resolve
  // after this composable mounts; without it an enabled account would miss the
  // initial load and behave differently from before.
  watch([() => currentChat.value?.id, isBatchingAvailable], loadMessageBatch, {
    immediate: true,
  });
  onMounted(() => emitter.on(MESSAGE_BATCH_UPDATED_EVENT, loadMessageBatch));
  onBeforeUnmount(() =>
    emitter.off(MESSAGE_BATCH_UPDATED_EVENT, loadMessageBatch)
  );

  return {
    messageBatch,
    messageBatchBusy,
    isBatchingAvailable,
    loadMessageBatch,
    flushBatch,
    discardBatch,
    updateBatch,
  };
}
